import type {
  PublicBookingConsent,
  PublicBookingConsentDocuments,
  PublicConsentRequirement,
  PublicIntakeConsent,
} from "@emr/contracts";

/**
 * What the patient decided on the agreement lines, as the page holds it. The terms box is ticked
 * for one version: when the clinic publishes new text and the page reloads it, the box shows
 * unticked again, so an agreement is only ever sent for the text on screen.
 */
export interface ConsentChoices {
  /** The terms version the patient ticked the box for; null while unticked. */
  readonly termsAcceptedVersion: number | null;
  /** The messaging line is shown ticked; unticking it declines. */
  readonly messagingAccepted: boolean;
}

export const initialConsentChoices: ConsentChoices = {
  termsAcceptedVersion: null,
  messagingAccepted: true,
};

export const termsRequiredToBook = "Agree to the terms and privacy notice to book";
export const termsRequiredToSubmit =
  "Agree to the terms and privacy notice to submit your questionnaire";
/** The clinic published new terms after the page loaded; nothing was saved. */
export const termsChanged =
  "The clinic has updated its terms and privacy notice. Read them, then tick the box again.";

/**
 * What the booking review says about messages at a clinic that offers them (PA-8). The clinic
 * chooses which messages it sends (a confirmation, reminders, or neither) and may change that,
 * so this promises none in particular. It follows the clinic's messaging notice: messages about
 * the patient's appointments, by email or text, only with the agreement on the line above.
 */
export const messagingOfferedDisclosure =
  "If you agree to messages above, the clinic can email or text you about your appointments, such as a booking confirmation or a reminder. Keep this page and your private link too.";

/** True when nothing asks for the terms, or the box is ticked for the version on screen. */
export function termsAgreed(
  documents: Pick<PublicConsentRequirement, "privacyNotice">,
  choices: ConsentChoices,
): boolean {
  return (
    documents.privacyNotice === null ||
    choices.termsAcceptedVersion === documents.privacyNotice.version
  );
}

/**
 * A booking's consent: the ticked terms, and the messaging line only when it was shown. A line
 * a reloaded configuration no longer asks for (the clinic stopped offering messages) is never
 * sent, whatever the box held before.
 */
export function bookingConsent(
  documents: PublicBookingConsentDocuments,
  choices: ConsentChoices,
): PublicBookingConsent | null {
  if (!termsAgreed(documents, choices)) return null;
  return {
    privacyNotice: { version: documents.privacyNotice.version, accepted: true },
    ...(documents.electronicMessaging === null
      ? {}
      : {
          electronicMessaging: {
            version: documents.electronicMessaging.version,
            accepted: choices.messagingAccepted,
          },
        }),
  };
}

/**
 * A questionnaire's consent: an answer to each document the session asked about, or undefined
 * when it asked about none. Call it only once termsAgreed holds. A session asks the messaging
 * line only while no decision is on file for the booking or its chart (PA-8), so a reread
 * session that stopped asking it gets no messaging answer, whatever the box held before.
 */
export function intakeConsent(
  documents: PublicConsentRequirement,
  choices: ConsentChoices,
): PublicIntakeConsent | undefined {
  const { privacyNotice, electronicMessaging } = documents;
  if (privacyNotice === null && electronicMessaging === null) return undefined;
  return {
    ...(privacyNotice === null
      ? {}
      : { privacyNotice: { version: privacyNotice.version, accepted: true } }),
    ...(electronicMessaging === null
      ? {}
      : {
          electronicMessaging: {
            version: electronicMessaging.version,
            accepted: choices.messagingAccepted,
          },
        }),
  };
}
